// Deferred destructive actions. Factory-owned.
//
// `remove()` hides the thing immediately and does the irreversible part only
// after the window closes. That ordering is the point: the app feels instant,
// and nothing is actually lost until it is too late to change your mind.
//
// A second `remove()` while one is pending COMMITS the first rather than
// dropping it. Deleting two things quickly is ordinary; silently keeping one of
// them because the second arrived first is not.

import { ref } from 'vue';
import { announce } from './a11y.js';

export const pending = ref(null);
let timer = 0;

/** Run the pending commit now, if there is one. */
export function flush() {
  const p = pending.value;
  if (!p) return;
  clearTimeout(timer);
  pending.value = null;
  try { p.commit?.(); } catch (e) { console.error('undo:commit', e); }
}

/**
 * Do something reversible, and offer to take it back.
 *
 * `commit` runs when the window closes; `revert` runs if Undo is pressed.
 * Whatever hides the item from the screen should happen BEFORE calling this.
 */
export function remove({ text = 'Deleted.', commit, revert, ms = 7000 } = {}) {
  flush();
  pending.value = { text, commit, revert };
  announce(`${text} Undo is available.`);
  timer = setTimeout(flush, ms);
}

export function undo() {
  const p = pending.value;
  if (!p) return;
  clearTimeout(timer);
  pending.value = null;
  try { p.revert?.(); } catch (e) { console.error('undo:revert', e); }
  announce('Restored.');
}

// A pending delete must not survive the page going away. Committing on unload
// is the honest choice: the user saw it happen and did not take it back, and
// the alternative is an item that reappears on next launch.
if (typeof addEventListener === 'function') {
  addEventListener('pagehide', flush);
}
